import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Badge from "@/components/base/Badge";
import DataTable, { type DataTableColumn } from "@/components/base/DataTable";
import EmptyState from "@/components/base/EmptyState";
import ExportMenu from "@/components/base/ExportMenu";
import { Input, Select } from "@/components/base/FormControls";
import StatCard from "@/pages/proprietaire/components/StatCard";
import { useAgenceData } from "@/pages/agence/data/AgenceDataProvider";
import {
  AGENCE_BIEN_TYPE_LABELS,
  AGENCE_LOYER_STATUT_LABELS,
  AGENCE_LOYER_STATUT_OPTIONS,
  AGENCE_LOYER_STATUT_TONES,
  agenceContratReference,
  agenceLocataireNomComplet,
  type AgenceLoyer,
} from "@/pages/agence/data/types";
import { useProprietaires } from "@/pages/agence/proprietaires/ProprietairesProvider";
import { proprietaireNomComplet } from "@/pages/agence/proprietaires/types";
import { monthLabel } from "@/pages/agence/loyers/periods";
import type { ExportColumn } from "@/utils/export";
import { formatDate, formatFcfa } from "@/utils/format";

type SortKey = "recent" | "ancien" | "montant";

const sortOptions = [
  { value: "recent", label: "Période récente" },
  { value: "ancien", label: "Période ancienne" },
  { value: "montant", label: "Montant décroissant" },
];

export default function AgenceLoyersPage() {
  const { loyers, statsLoyers, periodes, contrats, immeubles, biens, locataires, getBien, getLocataire, getImmeuble } =
    useAgenceData();
  const { proprietaires } = useProprietaires();
  const [searchParams] = useSearchParams();

  const [search, setSearch] = useState("");
  const [periodeFilter, setPeriodeFilter] = useState(searchParams.get("periode") ?? "");
  const [proprietaireFilter, setProprietaireFilter] = useState(searchParams.get("proprietaire") ?? "");
  const [immeubleFilter, setImmeubleFilter] = useState(searchParams.get("immeuble") ?? "");
  const [bienFilter, setBienFilter] = useState(searchParams.get("bien") ?? "");
  const [locataireFilter, setLocataireFilter] = useState(searchParams.get("locataire") ?? "");
  const [statutFilter, setStatutFilter] = useState("");
  const [sort, setSort] = useState<SortKey>("recent");

  const hasContratsActifs = contrats.some((contrat) => contrat.statut === "actif");
  const hasLoyers = loyers.length > 0;

  const periodeOptions = useMemo(
    () => [{ value: "", label: "Toutes les périodes" }, ...periodes.map((p) => ({ value: p, label: monthLabel(p) }))],
    [periodes]
  );

  const proprietaireOptions = useMemo(
    () => [
      { value: "", label: "Tous les propriétaires" },
      ...proprietaires.map((item) => ({ value: item.id, label: proprietaireNomComplet(item) })),
    ],
    [proprietaires]
  );

  const immeubleOptions = useMemo(
    () => [
      { value: "", label: "Tous les immeubles" },
      ...immeubles
        .filter((immeuble) => !proprietaireFilter || immeuble.proprietaireId === proprietaireFilter)
        .map((immeuble) => ({ value: immeuble.id, label: immeuble.nom })),
    ],
    [immeubles, proprietaireFilter]
  );

  const bienOptions = useMemo(
    () => [
      { value: "", label: "Tous les biens" },
      ...biens
        .filter((bien) => !immeubleFilter || bien.immeubleId === immeubleFilter)
        .filter((bien) => !proprietaireFilter || bien.proprietaireId === proprietaireFilter)
        .map((bien) => ({
          value: bien.id,
          label: `${AGENCE_BIEN_TYPE_LABELS[bien.type]}${bien.reference ? ` · ${bien.reference}` : ""}`,
        })),
    ],
    [biens, immeubleFilter, proprietaireFilter]
  );

  const locataireOptions = useMemo(
    () => [
      { value: "", label: "Tous les locataires" },
      ...locataires.map((locataire) => ({ value: locataire.id, label: agenceLocataireNomComplet(locataire) })),
    ],
    [locataires]
  );

  const statutOptions = useMemo(() => [{ value: "", label: "Tous les statuts" }, ...AGENCE_LOYER_STATUT_OPTIONS], []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const result = loyers.filter((loyer) => {
      const matchesPeriode = !periodeFilter || loyer.periode === periodeFilter;
      const matchesProprietaire = !proprietaireFilter || loyer.proprietaireId === proprietaireFilter;
      const matchesImmeuble = !immeubleFilter || loyer.immeubleId === immeubleFilter;
      const matchesBien = !bienFilter || loyer.bienId === bienFilter;
      const matchesLocataire = !locataireFilter || loyer.locataireId === locataireFilter;
      const matchesStatut = !statutFilter || loyer.statut === statutFilter;

      const bien = getBien(loyer.bienId);
      const locataire = getLocataire(loyer.locataireId);
      const immeuble = getImmeuble(loyer.immeubleId);
      const matchesSearch =
        !term ||
        [
          monthLabel(loyer.periode),
          bien ? AGENCE_BIEN_TYPE_LABELS[bien.type] : "",
          bien?.reference ?? "",
          locataire ? agenceLocataireNomComplet(locataire) : "",
          immeuble?.nom ?? "",
          AGENCE_LOYER_STATUT_LABELS[loyer.statut],
        ]
          .join(" ")
          .toLowerCase()
          .includes(term);

      return (
        matchesPeriode &&
        matchesProprietaire &&
        matchesImmeuble &&
        matchesBien &&
        matchesLocataire &&
        matchesStatut &&
        matchesSearch
      );
    });

    return result.sort((a, b) => {
      if (sort === "montant") return b.montant - a.montant;
      if (sort === "ancien") return a.periode < b.periode ? -1 : 1;
      return a.periode < b.periode ? 1 : -1;
    });
  }, [
    loyers,
    search,
    periodeFilter,
    proprietaireFilter,
    immeubleFilter,
    bienFilter,
    locataireFilter,
    statutFilter,
    sort,
    getBien,
    getLocataire,
    getImmeuble,
  ]);

  const isFiltering =
    Boolean(search.trim()) ||
    Boolean(periodeFilter) ||
    Boolean(proprietaireFilter) ||
    Boolean(immeubleFilter) ||
    Boolean(bienFilter) ||
    Boolean(locataireFilter) ||
    Boolean(statutFilter);

  const resetFilters = () => {
    setSearch("");
    setPeriodeFilter("");
    setProprietaireFilter("");
    setImmeubleFilter("");
    setBienFilter("");
    setLocataireFilter("");
    setStatutFilter("");
  };

  const exportColumns: ExportColumn<AgenceLoyer>[] = [
    { header: "Période", value: (loyer) => monthLabel(loyer.periode) },
    {
      header: "Propriétaire",
      value: (loyer) => {
        const owner = proprietaires.find((item) => item.id === loyer.proprietaireId);
        return owner ? proprietaireNomComplet(owner) : "";
      },
    },
    { header: "Immeuble", value: (loyer) => getImmeuble(loyer.immeubleId)?.nom ?? "" },
    {
      header: "Bien",
      value: (loyer) => {
        const bien = getBien(loyer.bienId);
        return bien ? `${AGENCE_BIEN_TYPE_LABELS[bien.type]}${bien.reference ? ` ${bien.reference}` : ""}` : "";
      },
    },
    {
      header: "Locataire",
      value: (loyer) => {
        const locataire = getLocataire(loyer.locataireId);
        return locataire ? agenceLocataireNomComplet(locataire) : "";
      },
    },
    { header: "Montant (FCFA)", value: (loyer) => formatFcfa(loyer.montant) },
    { header: "Payé (FCFA)", value: (loyer) => formatFcfa(loyer.totalPaye) },
    { header: "Reste (FCFA)", value: (loyer) => formatFcfa(loyer.reste) },
    { header: "Échéance", value: (loyer) => formatDate(loyer.echeance) },
    { header: "Statut", value: (loyer) => AGENCE_LOYER_STATUT_LABELS[loyer.statut] },
  ];

  const columns: DataTableColumn<AgenceLoyer>[] = [
    {
      key: "periode",
      header: "Période",
      className: "min-w-[120px]",
      render: (loyer) => (
        <span className="font-label font-medium text-foreground-950">{monthLabel(loyer.periode)}</span>
      ),
    },
    {
      key: "bien",
      header: "Bien",
      className: "min-w-[170px]",
      render: (loyer) => {
        const bien = getBien(loyer.bienId);
        return (
          <span className="text-foreground-700">
            {bien ? AGENCE_BIEN_TYPE_LABELS[bien.type] : "Non renseigné"}
            {bien?.reference ? ` · ${bien.reference}` : ""}
          </span>
        );
      },
    },
    {
      key: "locataire",
      header: "Locataire",
      className: "min-w-[170px]",
      render: (loyer) => {
        const locataire = getLocataire(loyer.locataireId);
        return <span className="text-foreground-700">{locataire ? agenceLocataireNomComplet(locataire) : "—"}</span>;
      },
    },
    {
      key: "immeuble",
      header: "Immeuble",
      className: "min-w-[150px]",
      render: (loyer) => <span className="text-foreground-700">{getImmeuble(loyer.immeubleId)?.nom ?? "—"}</span>,
    },
    {
      key: "montant",
      header: "Montant",
      align: "right",
      className: "min-w-[110px]",
      render: (loyer) => <span className="font-label font-medium text-foreground-950">{formatFcfa(loyer.montant)}</span>,
    },
    {
      key: "reste",
      header: "Reste",
      align: "right",
      className: "min-w-[110px]",
      render: (loyer) => (
        <span
          className={
            loyer.statut === "paye" ? "font-label font-medium text-secondary-700" : "font-label font-medium text-primary-700"
          }
        >
          {formatFcfa(loyer.reste)}
        </span>
      ),
    },
    {
      key: "echeance",
      header: "Échéance",
      className: "min-w-[120px]",
      render: (loyer) => <span className="text-foreground-700">{formatDate(loyer.echeance)}</span>,
    },
    {
      key: "statut",
      header: "Statut",
      render: (loyer) => (
        <Badge tone={AGENCE_LOYER_STATUT_TONES[loyer.statut]}>{AGENCE_LOYER_STATUT_LABELS[loyer.statut]}</Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      className: "min-w-[230px]",
      render: (loyer) => (
        <div className="flex items-center justify-end gap-2">
          {loyer.statut !== "paye" && (
            <Link
              to={`/agence/paiements?contrat=${loyer.contratId}&periode=${loyer.periode}&nouveau=1`}
              className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-3.5 py-2 text-xs font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-hand-coin-line text-base" aria-hidden="true" />
              Paiement
            </Link>
          )}
          <Link
            to={`/agence/contrats/${loyer.contratId}`}
            className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2 text-xs font-label font-medium text-foreground-900 transition-colors hover:border-foreground-300 hover:bg-background-100 cursor-pointer"
          >
            {agenceContratReference(loyer.contratId)}
            <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
          </Link>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-2xl">
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">Loyers</h2>
          <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
            Les loyers proviennent de vos contrats actifs : une échéance par mois, avec le montant, la période et le
            statut de règlement. Encaissez un loyer pour le passer à « Payé ».
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <ExportMenu rows={filtered} columns={exportColumns} filename="agence-loyers" title="Loyers" />
          <Link
            to="/agence/paiements?nouveau=1"
            className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-hand-coin-line text-base" aria-hidden="true" />
            Enregistrer un paiement
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon="ri-checkbox-circle-line" label="Loyers payés" value={String(statsLoyers.payes)} tone="secondary" />
        <StatCard icon="ri-calendar-schedule-line" label="À venir" value={String(statsLoyers.aVenir)} tone="neutral" />
        <StatCard icon="ri-timer-line" label="En retard" value={String(statsLoyers.enRetard)} tone="accent" />
        <StatCard icon="ri-alert-line" label="Impayés" value={String(statsLoyers.impayes)} tone="primary" />
      </div>

      {hasLoyers && (
        <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
            <div className="flex flex-col gap-1.5 lg:col-span-2">
              <label htmlFor="agence-loy-search" className="text-xs font-label font-medium text-foreground-700">
                Recherche
              </label>
              <Input
                id="agence-loy-search"
                icon="ri-search-line"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Période, bien, locataire, immeuble..."
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="agence-loy-periode" className="text-xs font-label font-medium text-foreground-700">
                Période
              </label>
              <Select
                id="agence-loy-periode"
                value={periodeFilter}
                onChange={(event) => setPeriodeFilter(event.target.value)}
                options={periodeOptions}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="agence-loy-statut" className="text-xs font-label font-medium text-foreground-700">
                Statut
              </label>
              <Select
                id="agence-loy-statut"
                value={statutFilter}
                onChange={(event) => setStatutFilter(event.target.value)}
                options={statutOptions}
              />
            </div>
            <div className="flex flex-col gap-1.5 lg:col-span-2">
              <label htmlFor="agence-loy-proprietaire" className="text-xs font-label font-medium text-foreground-700">
                Propriétaire
              </label>
              <Select
                id="agence-loy-proprietaire"
                value={proprietaireFilter}
                onChange={(event) => {
                  setProprietaireFilter(event.target.value);
                  setImmeubleFilter("");
                  setBienFilter("");
                }}
                options={proprietaireOptions}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="agence-loy-immeuble" className="text-xs font-label font-medium text-foreground-700">
                Immeuble
              </label>
              <Select
                id="agence-loy-immeuble"
                value={immeubleFilter}
                onChange={(event) => {
                  setImmeubleFilter(event.target.value);
                  setBienFilter("");
                }}
                options={immeubleOptions}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="agence-loy-bien" className="text-xs font-label font-medium text-foreground-700">
                Bien
              </label>
              <Select
                id="agence-loy-bien"
                value={bienFilter}
                onChange={(event) => setBienFilter(event.target.value)}
                options={bienOptions}
              />
            </div>
            <div className="flex flex-col gap-1.5 lg:col-span-2">
              <label htmlFor="agence-loy-locataire" className="text-xs font-label font-medium text-foreground-700">
                Locataire
              </label>
              <Select
                id="agence-loy-locataire"
                value={locataireFilter}
                onChange={(event) => setLocataireFilter(event.target.value)}
                options={locataireOptions}
              />
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-3 border-t border-background-200 pt-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-foreground-500">
              {filtered.length} loyer{filtered.length > 1 ? "s" : ""} affiché{filtered.length > 1 ? "s" : ""} sur{" "}
              {loyers.length}
            </p>
            <div className="flex items-center gap-3">
              {isFiltering && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
                >
                  <i className="ri-refresh-line text-sm" aria-hidden="true" />
                  Réinitialiser
                </button>
              )}
              <div className="w-44">
                <Select
                  aria-label="Trier par"
                  value={sort}
                  onChange={(event) => setSort(event.target.value as SortKey)}
                  options={sortOptions}
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {!hasContratsActifs && (
        <EmptyState
          icon="ri-wallet-3-line"
          title="Aucun loyer à suivre"
          description="Les loyers proviennent des contrats actifs. Créez un contrat actif pour générer automatiquement les échéances mensuelles."
          action={
            <Link
              to="/agence/contrats"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-file-text-line text-base" aria-hidden="true" />
              Créer un contrat
            </Link>
          }
        />
      )}

      {hasContratsActifs && filtered.length === 0 && (
        <EmptyState
          icon={isFiltering ? "ri-search-2-line" : "ri-wallet-3-line"}
          title={isFiltering ? "Aucun résultat" : "Aucun loyer"}
          description={
            isFiltering
              ? "Aucun loyer ne correspond à votre recherche ou aux filtres sélectionnés. Essayez de modifier vos critères."
              : "Les échéances de vos contrats actifs apparaîtront ici dès la date de début d'un contrat."
          }
          action={
            isFiltering ? (
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-refresh-line text-base" aria-hidden="true" />
                Réinitialiser les filtres
              </button>
            ) : undefined
          }
        />
      )}

      {filtered.length > 0 && (
        <DataTable
          columns={columns}
          rows={filtered}
          rowKey={(loyer) => loyer.id}
          minWidthClass="min-w-[1280px]"
          caption="Loyers"
        />
      )}
    </div>
  );
}